// ═══════════════════════════════════════════════════════════════════
// bonnieModel3D.jsx — BONNIE, SCULPTED   (caP'n, 2026-09-24)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "make my character Bonnie a blender 3D model … the mouth area to be a
// stitched lion (tiger with no stripes) snout instead of her real nose and mouth
// … instead of Mystic Ally setting up shop at the UP, we are going to put our
// lovely Bonnie there … just have Bonnie stand next to wagon".
// public/models/bonnie.glb — master gamemaster/models/sculpt/glb/bonnie.glb,
// builder gamemaster/models/sculpt/builders/bonnie_blender/ (forked from Venus's:
// same 21-bone rig and loader contract, INK_BY_PART kept). Metres, feet at 0,
// facing +z. A first pass: expect iteration.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const BONNIE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/bonnie.glb`;
const MODEL_TOP = 1.658;     // measured at export (Blender build, 2026-09-24): the bunny ear's fold
// THE INK HULL'S WIDTH, PER PART — mirrored in the builder's ven_look.py (INK_BY_PART).
// Thin parts get a thinner line (a full-width hull is wider than the part); 0 = no line.
const INK_W = 0.0032;
const INK_BY_PART = [[/stitches/, 0], [/laces/, 0.0012], [/cord/, 0.0014], [/cards/, 0.0014], [/hoop/, 0.0010],
  [/flowers/, 0.0014], [/cross/, 0.0016], [/tuft/, 0.0024]];
const inkWidth = (name) => (INK_BY_PART.find(([re]) => re.test(name)) || [null, INK_W])[1];
const SELF_LIGHT = 0.4;

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
const INK = new Map();
const inkMat = (w) => {
  let m = INK.get(w);
  if (m) return m;
  m = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>",
      "#include <begin_vertex>\n  transformed += normalize( normal ) * " + w.toFixed(5) + ";");
  };
  m.customProgramCacheKey = () => "bonnieInk" + w;     // two widths share a signature without this
  INK.set(w, m);
  return m;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    // the patchwork lives in the vertex colours: the self-light must be scaled by them
    // too, or it washes every patch toward the same grey
    if (vc) {
      o.material.onBeforeCompile = (sh) => {
        sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;",
          "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
      };
      o.material.customProgramCacheKey = () => "bonnieVC";
    }
    o.frustumCulled = false;
    const w = inkWidth(o.name);
    if (w > 0) {
      const ink = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat(w)) : new THREE.Mesh(o.geometry, inkMat(w));
      if (o.isSkinnedMesh) ink.bind(o.skeleton, o.bindMatrix);
      ink.name = o.name + "_ink"; ink.frustumCulled = false;
      o.parent.add(ink);
    }
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B };
}

// the "over here!" wave: a 2.2 s window every 7 s, eased in and out
// She stands by the wagon: arms hanging, a slow sway, the head tilting as she eyes the passers-by,
// the tail swishing. (Walk kept for later — the idle is what the pier shows.)
export function bonniePose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const still = 1 - amt, sw = Math.sin(ph);
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.30 - swing * 0.10) * amt, 0, 0);
    set("shin" + k, swing * 0.55 * amt, 0, 0);
    set("foot" + k, (-swing * 0.25 + Math.max(0, -fwd) * 0.15) * amt, 0, 0);
    const breathe = Math.sin(t * 1.3 + (s > 0 ? 0 : 1.1)) * 0.025;
    set("upperarm" + k, breathe + fwd * 0.30 * amt, 0, s * 0.03 * still);
    set("forearm" + k, -0.12 * still - 0.2 * amt, 0, 0);
    set("hand" + k, 0.05 * still, 0, 0);
  }
  set("hips", 0, sw * 0.05 * amt, Math.sin(t * 0.6) * 0.025 * still);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.016 * amt;
  set("spine", 0, -sw * 0.04 * amt, -Math.sin(t * 0.6) * 0.02 * still);
  set("chest", Math.sin(t * 1.3) * 0.012, -sw * 0.03 * amt, 0);
  set("neck", 0, 0, 0);
  set("head", -0.02 + Math.sin(t * 0.8) * 0.015, Math.sin(t * 0.3) * 0.25 * (1 - 0.6 * amt), 0.09 + Math.sin(t * 0.45) * 0.04);
  set("tail1", Math.sin(t * 0.9) * 0.04, Math.sin(t * 1.1) * 0.12, 0);
  set("tail2", 0, Math.sin(t * 1.1 - 0.7) * 0.18, Math.sin(t * 0.8) * 0.05);
  set("tail3", Math.sin(t * 1.3 - 1.2) * 0.08, Math.sin(t * 1.1 - 1.4) * 0.26, 0);
}

export function BonnieModel({ ctl, h = 1.6 }) {
  const gltf = useLoader(GLTFLoader, BONNIE_GLB_URL);
  const { root, B } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0);
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 3.1;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 7;
    bonniePose(B, t, ph.current, amt.current);
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / MODEL_TOP} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[bonnie] bonnie.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function BonnieSculpted({ ctl, h, Fallback }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <BonnieModel ctl={ctl} h={h} />
      </Suspense>
    </GlbBoundary>
  );
}

export default BonnieModel;
